import { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Layers, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { fmtAxv, IMAGES } from "../lib/tokens";
import { VELLUM_RECT } from "../lib/beats";
import { useWorld } from "../state/World";
import LiveToggle from "../components/LiveToggle";
import FeeLines, { Receipt } from "../components/FeeLines";
import StackPanel from "../components/StackPanel";

// Scattered on vellum, deterministic per Shard so nothing jumps on re-render.
const hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

export default function Table() {
  const { refresh, mark, isLive } = useWorld();
  const nav = useNavigate();
  const [shards, setShards] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [stacks, setStacks] = useState([]);
  const [quote, setQuote] = useState(null);
  const [tx, setTx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState(null);
  const [liveSpent, setLiveSpent] = useState(false);
  const [openId, setOpenId] = useState(null); // cluster being read
  const [stack, setStack] = useState(null); // the Stack being sequenced

  const load = useCallback(async () => {
    const { data } = await api.get("/stageward/project");
    setShards(data.shards || []);
    setClusters(data.clusters || []);
    setStacks(data.stacks || []);
  }, []);

  const reQuote = useCallback(async () => {
    const { data } = await api.get("/stageward/quote", { params: { live: isLive() } });
    setQuote(data);
  }, [isLive]);

  useEffect(() => {
    mark("stageward", "/stageward/table");
    load();
    reQuote();
  }, [mark, load, reQuote]);

  const gather = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stageward/cluster", { live: isLive() });
      setClusters(data.clusters);
      setTx(data.transaction);
      setSource(data.source);
      setLiveSpent(Boolean(data.live_spent));
      window.dispatchEvent(new Event("melz-live-used"));
      setStack(null);
      await refresh();
      await load();
      await reQuote();
      toast.success(`${fmtAxv(data.transaction.total_sub)} Tokens left the Pouch.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The Table would not take it.");
    } finally {
      setBusy(false);
    }
  };

  const isolate = async (clusterId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/stageward/stack", {
        cluster_id: clusterId,
        live: isLive(),
      });
      setStack(data.stack);
      setLiveSpent(Boolean(data.live_spent));
      window.dispatchEvent(new Event("melz-live-used"));
      setOpenId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That Cluster would not isolate.");
    } finally {
      setBusy(false);
    }
  };

  const move = (i, dir) => {
    const next = [...stack.ordered_shard_ids];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setStack({ ...stack, ordered_shard_ids: next, sequenced_by: "you" });
  };

  const saveSequence = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/stageward/stack/${stack.id}`, {
        ordered_shard_ids: stack.ordered_shard_ids,
      });
      setStack(data.stack);
      await load();
      toast.success("The Sequence is yours.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The Sequence would not save.");
    } finally {
      setBusy(false);
    }
  };

  const setMission = async (clusterId) => {
    setBusy(true);
    try {
      await api.post("/stageward/mission", { cluster_id: clusterId });
      nav("/stageward/mission");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mission would not set.");
    } finally {
      setBusy(false);
    }
  };

  const textOf = useMemo(
    () => Object.fromEntries(shards.map((s) => [s.id, s.text])),
    [shards]
  );
  const open = clusters.find((c) => c.id === openId);
  const stacked = new Set(stacks.map((s) => s.cluster_id));

  const inset = {
    left: `${VELLUM_RECT.left}%`,
    right: `${100 - VELLUM_RECT.right}%`,
    top: `${VELLUM_RECT.top}%`,
    bottom: `${100 - VELLUM_RECT.bottom}%`,
  };

  return (
    <div className="relative h-screen overflow-hidden bg-[#0d0b07]" data-testid="drawing-table">
      <img
        src={IMAGES.swDrawingSurface}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
      />

      <div className="absolute left-6 top-5 z-40 flex items-center gap-5">
        <button
          type="button"
          data-testid="back-to-coat-closet"
          onClick={() => nav("/stageward")}
          className="tracked flex items-center gap-2 text-[9px] text-[#d9c69a] transition-colors duration-200 hover:text-[var(--stage-gold)]"
        >
          <ArrowLeft size={12} /> The Graffiti Wall
        </button>
        <LiveToggle accent="gold" surface="gather" />
      </div>

      {/* everything happens on the vellum, nowhere else */}
      <div className="absolute z-20 overflow-y-auto" style={inset}>
        {shards.length === 0 ? (
          <div className="grid h-full place-items-center px-8" data-testid="table-empty">
            <div className="max-w-md text-center">
              <div className="serif mb-3 text-3xl font-light text-[#3f3117]">
                The vellum is blank.
              </div>
              <p className="mb-7 text-sm leading-relaxed text-[#6b5636]">
                MelzAI has nothing to lay out. Go back to the wall and put it down first
                — at least three Shards.
              </p>
              <button
                type="button"
                data-testid="table-to-coat-closet-button"
                onClick={() => nav("/stageward")}
                className="btn btn-gold"
              >
                Back to the wall
              </button>
            </div>
          </div>
        ) : clusters.length === 0 ? (
          /* ---------- laid out loose, waiting on MelzAI's offer ---------- */
          <div className="relative min-h-full px-8 py-8" data-testid="table-shards">
            <div className="tracked mb-6 text-[9px] text-[#6b5636]">
              Off the wall · {shards.length} Shards · not yet gathered
            </div>
            <div className="columns-2 gap-8 sm:columns-3 lg:columns-4">
              {shards.map((s, i) => {
                const h = hash(s.id || s.text);
                return (
                  <motion.div
                    key={s.id}
                    className="mb-4 break-inside-avoid"
                    initial={{ opacity: 0, y: -30, rotate: 0 }}
                    animate={{ opacity: 1, y: 0, rotate: ((h % 400) / 400 - 0.5) * 5 }}
                    transition={{ duration: 0.7, delay: i * 0.05, ease: [0.2, 0.8, 0.2, 1] }}
                  >
                    <span className="scrawl block text-[19px] leading-tight text-[#4a3a1d]">
                      {s.text}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ) : (
          /* ---------- gathered into piles ---------- */
          <div className="px-8 py-8" data-testid="cluster-list">
            <div className="tracked mb-7 flex flex-wrap items-center gap-x-4 gap-y-1 text-[9px] text-[#6b5636]">
              <span>
                {clusters.length} Clusters ·{" "}
                {liveSpent
                  ? "live proven here · back to Demo Mode"
                  : source === "live"
                  ? "live Claude call"
                  : source
                  ? "Demo Mode"
                  : "from earlier"}
              </span>
              <button
                type="button"
                data-testid="recluster-button"
                disabled={busy}
                onClick={gather}
                className="mono text-[9px] text-[#8a7148] transition-colors hover:text-[#3a2d18]"
              >
                → gather again (spends again)
              </button>
            </div>

            <div className="flex flex-wrap items-start gap-x-10 gap-y-12">
              {clusters.map((c, i) => (
                <motion.button
                  key={c.id}
                  type="button"
                  data-testid="cluster-card"
                  onClick={() => setOpenId(openId === c.id ? null : c.id)}
                  initial={{ opacity: 0, scale: 0.8, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.12, ease: [0.2, 0.8, 0.2, 1] }}
                  className="group relative w-[210px] text-left"
                >
                  {/* the pile: layered cards, so it reads as a stack of paper */}
                  <span
                    aria-hidden
                    className="absolute inset-0 rounded-lg border border-black/15 bg-[#efe2c4] transition-transform duration-300 group-hover:-rotate-[4deg]"
                    style={{ transform: "rotate(-2.5deg)" }}
                  />
                  <span
                    aria-hidden
                    className="absolute inset-0 rounded-lg border border-black/15 bg-[#f3e8ce] transition-transform duration-300 group-hover:rotate-[3deg]"
                    style={{ transform: "rotate(1.5deg)" }}
                  />
                  <span
                    className="relative block rounded-lg border border-black/20 bg-[#f8f0dc] p-5 shadow-[0_10px_28px_-14px_rgba(0,0,0,0.6)] transition-transform duration-300 group-hover:-translate-y-1.5"
                  >
                    <span className="serif block text-[19px] leading-tight text-[#33280f]">
                      {c.title}
                    </span>
                    <span className="mono mt-2 block text-[9px] text-[#8a7148]">
                      {c.shard_ids.length} Shards
                      {stacked.has(c.id) && " · sequenced"}
                    </span>
                    {c.rationale && (
                      <span className="mt-3 block text-[11.5px] italic leading-snug text-[#6b5636]">
                        {c.rationale}
                      </span>
                    )}
                  </span>
                </motion.button>
              ))}
            </div>

            {/* opening a pile: read it, then isolate it to set the Sequence */}
            <AnimatePresence>
              {open && (
                <motion.div
                  className="glass-warm mt-12 max-w-2xl rounded-2xl p-7"
                  data-testid="cluster-open"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="serif mb-4 text-2xl font-light text-[#f7ecd8]">
                    {open.title}
                  </div>
                  <div className="mb-6 space-y-1.5">
                    {open.shard_ids.map((sid) => (
                      <div key={sid} className="text-[13px] leading-snug text-white/55">
                        · {textOf[sid]}
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <button
                      type="button"
                      data-testid="isolate-stack-button"
                      disabled={busy}
                      onClick={() => isolate(open.id)}
                      className="btn btn-gold"
                    >
                      {busy ? (
                        <Loader2 className="animate-spin" size={14} />
                      ) : (
                        <span className="flex items-center gap-2">
                          <Layers size={13} /> Isolate as a Stack · free
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      data-testid="cluster-set-mission"
                      disabled={busy}
                      onClick={() => setMission(open.id)}
                      className="mono text-[10px] text-white/35 transition-colors hover:text-[var(--stage-gold)]"
                    >
                      → make this the Mission
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* ---------- MelzAI's offer. He proposes, you decide. ---------- */}
      <AnimatePresence>
        {clusters.length === 0 && shards.length >= 3 && quote && (
          <motion.div
            className="absolute inset-x-0 bottom-0 z-30 flex justify-center px-6 pb-7"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.7, delay: 0.6 }}
          >
            <div className="glass w-full max-w-xl rounded-2xl p-6" data-testid="melz-offer">
              <div className="tracked mb-2 text-[9px] text-[var(--stage-gold)]/80">
                MelzAI offers
              </div>
              <p className="mb-5 text-[14px] leading-relaxed text-white/70">
                {quote.gather.buys} That is real compute, so it is a real spend. Here is
                the whole bill before you agree to it — and putting a Cluster in order
                afterwards costs you nothing.
              </p>
              <div className="mb-5 rounded-xl border border-white/8 bg-black/30 p-4">
                <FeeLines
                  principalLabel="MelzAI gathers into Clusters"
                  principalSub={quote.gather.principal_sub}
                  feeLines={quote.gather.fee_lines}
                  totalSub={quote.gather.total_sub}
                  testid="cluster-fee-lines"
                />
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  data-testid="cluster-spend-button"
                  disabled={busy || !quote.affordable}
                  onClick={gather}
                  className="btn btn-gold"
                >
                  {busy ? (
                    <Loader2 className="mx-auto animate-spin" size={14} />
                  ) : (
                    `Worth it — spend ${fmtAxv(quote.gather.total_sub)}`
                  )}
                </button>
                <button
                  type="button"
                  data-testid="offer-decline"
                  onClick={() => nav("/stageward")}
                  className="mono text-[10px] text-white/30 transition-colors hover:text-white/70"
                >
                  → not yet, I have more to put down
                </button>
              </div>
              {!quote.affordable && (
                <p className="mono mt-3 text-[9px] text-[var(--stage-ember)]">
                  Not enough in the Pouch. Top up from the Pouch panel.
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---------- the Sequence ---------- */}
      <AnimatePresence>
        {stack && (
          <motion.div
            className="absolute inset-0 z-40 grid place-items-center overflow-y-auto bg-black/55 px-6 py-10 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <StackPanel
              stack={stack}
              textOf={textOf}
              busy={busy}
              onMove={move}
              onSave={saveSequence}
              onClose={() => setStack(null)}
              onMission={() => setMission(stack.cluster_id)}
              onDesk={() => nav("/stageward/desk")}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {tx && !stack && (
          <div className="absolute right-6 top-16 z-30 w-full max-w-xs">
            <Receipt tx={tx} />
          </div>
        )}
      </AnimatePresence>

      <button
        type="button"
        data-testid="table-ledger-link"
        onClick={() => nav("/ledger")}
        className="mono absolute bottom-5 left-6 z-30 text-[10px] text-[#c8b48a] transition-colors duration-200 hover:text-[var(--stage-gold)]"
      >
        → inspect the Ledger
      </button>
    </div>
  );
}
